import { CommonModule, JsonPipe } from '@angular/common';
import { Component, computed, input, OnDestroy, OnInit, Optional, Signal, signal } from '@angular/core';
import { Feature, Map as OlMap, View } from 'ol';
import { Geometry } from 'ol/geom';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { OSM, XYZ } from 'ol/source';
import IGC from 'ol/format/IGC.js';
import KML from 'ol/format/KML.js';
import GPX from 'ol/format/GPX.js';
import TopoJSON from 'ol/format/TopoJSON.js';
import GeoJSON from 'ol/format/GeoJSON.js';

import VectorSource from 'ol/source/Vector';
import { unzipSync } from 'fflate';
import { environment } from 'src/environments/environment';
import { transform } from 'ol/proj';
import { isHTML } from 'src/app/utils/string.utils';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MaterialModule } from 'src/app/shared/modules/material.module';
import { PageEvent } from '@angular/material/paginator';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { defaults as defaultInteractions } from 'ol/interaction';
import { ImportWizardService } from '../../../import-wizard.service';
import { ImportWizardStorage, ImportWizardStep, TabularColumns } from 'src/app/pages/project/map/repository/import-wizard/import-wizard.storage';
import { MatDialogRef } from '@angular/material/dialog';
import { ImportWizardComponent } from '../../../import-wizard.component';
import { ImportWizardTabularService } from '../import-wizard-tabular.service';
import { AlertComponent } from 'src/app/shared/alert/alert.component';
import { ToastrService } from 'ngx-toastr';
import { JobStatus } from 'src/app/dtos/import-wizard/import-wizard.dto';

@Component({
    selector: 'app-import-wizard-tabular-preview',
    templateUrl: './import-wizard-tabular-preview.component.html',
    styleUrls: ['./import-wizard-tabular-preview.component.scss'],
    imports: [
        CommonModule,
        MaterialModule,
        ScrollingModule,
        AlertComponent
    ],
    standalone: true
})
export class ImportWizardTabularPreviewComponent implements OnInit, OnDestroy {
    // WIZARD STATE
    _state = computed(() => this.importWizardStorage.state())

    // DATA
    tabularRows = computed(() => this.importWizardStorage.rows());
    tabularTableDataSource = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize();
        const end = start + this.pageSize();

        if(this.searchTerm() === '') return this.tabularRows().slice(start, end);

        return this.tabularRows().filter((row) => this.valueMatchesSearch(row)).slice(start, end);
    });
    fileTotalRows = computed(() => this.importWizardStorage.fileTotalRows());

    columns = computed(() => this.importWizardStorage.columns());
    columns_names = computed(() => this.columns().map((col) => col.name));
    columns_ids = computed(() => this.columns().map((_, i) => `col_${i}`));

    // PAGINATION
    pagesSizes = { 
        'min': 5,
        'mid-lower': 10,
        'mid-upper': 25,
        'max': 100
    }
    pagesSizesArray = Object.values(this.pagesSizes);
    pageSize = signal(this.pagesSizes['mid-lower']);
    currentPage = signal(1);
    total = computed(() => this.tabularRows().filter((row) => this.valueMatchesSearch(row)).length);

    private searchTerm = signal<string>('');

    isAlertVisible = false;
    loadingProgress = signal<number>(0);
    isDataLoading = signal<boolean>(true);
    private progressInterval?: any;

    constructor(
        public importWizardStorage: ImportWizardStorage,
        public importWizardService: ImportWizardService,
        private importWizardTabularService: ImportWizardTabularService,
        private sanitizer: DomSanitizer,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>,
        private toastrService: ToastrService
    ) {
    
    }

    ngOnInit() {
        this.initializeFile();
    }

    async initializeFile(): Promise<void> {
        if(this._state().step > ImportWizardStep.PREVIEW) {
            this.isDataLoading.set(false);
            return;
        }

        this.isDataLoading.set(true);
        this.startProgressSimulation();

        try {
            await this.importWizardTabularService.useGetImportDataPreview();
        } catch (error) {
            console.error('Erro ao processar arquivo:', error);
            alert('Erro ao processar o arquivo. Verifique o formato.');
        } finally {
            this.stopProgressSimulation();
            this.loadingProgress.set(100);
            setTimeout(() => {
                this.isDataLoading.set(false);
            }, 400);
        }
    }

    private startProgressSimulation(): void {
        this.stopProgressSimulation();
        this.loadingProgress.set(0);

        this.progressInterval = setInterval(() => {
            const current = this.loadingProgress();
            if (current < 99) {
                const step = (99 - current) * 0.012;
                const next = current + Math.max(step, 0.01);
                this.loadingProgress.set(Math.min(next, 99));
            } else {
                this.stopProgressSimulation();
            }
        }, 1000);
    }

    private stopProgressSimulation(): void {
        if (this.progressInterval) {
            clearInterval(this.progressInterval);
            this.progressInterval = undefined;
        }
    }

    ngOnDestroy(): void {
        this.stopProgressSimulation();
    }

    search(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.searchTerm.set(target.value);
    }

    handlePageChange(event: PageEvent): void {
        this.currentPage.set(event.pageIndex + 1);
        this.pageSize.set(event.pageSize);
    }

    valueMatchesSearch(row: any): boolean {
        const term = this.searchTerm().toLowerCase();

        return Object.values(row)
                .some(value => value!.toString().toLowerCase().includes(term));
        
    }

    handleButtonClick(): void {
        if(!this.isPreviewStep) {
            this.importWizardStorage.redirectInViewStep(ImportWizardStep.MAPPING_ADJUSTS);
            return;
        }
        
        this.importWizardService.patchImportWizard(this._state().id, JobStatus.PENDING).subscribe({ 
            next: () => {
                this.importWizardStorage.setNextStep();
            },
            error: () => {
                this.toastrService.error('Erro ao iniciar importação!', 'Erro');
            }
        });
    }

    handleCancelClick(): void {
        this.isAlertVisible = true;
    }

    handleCancelClickConfirmation(): void {
        this.isAlertVisible = false;

        this.importWizardStorage.handleCancelClickConfirmation(this.dialogRef, false);
    }

    handleCancelClickCancel(): void {
        this.isAlertVisible = false;
    }

    get isPreviewStep(): boolean {
        return this._state().step === ImportWizardStep.PREVIEW;
    }

    ImportWizardStep = ImportWizardStep;
}